import { getRandomPartialColor } from '$lib/helper/color';
import { generateUUID } from '$lib/helper/uuid';
import { activityStore } from '$lib/stores/activity';
import { areaStore } from '$lib/stores/area';
import type { DateTime } from 'luxon';
import { derived, get } from 'svelte/store';
import type { Area } from './area';

export type Session = {
	id: string;
	name: string;
	color: string;
	logo: string | null;
	followAllAreas: boolean;
	parentId: string;
	capacityDurations: Array<{ min: number; max: number; duration: number }>;
	preparationDuration: number;
	onboardingDuration: number;
	debriefingDuration: number;
	cleaningDuration: number;
	bookingTimeBefore: number;
	followLogo: boolean;
	areas: Array<string>;
	archivedAt: DateTime | null;
	interval: number;
	maxBooking: number;
};

export function getMinCustomerDuration(session: Session): number {
	return (
		Math.min(...session.capacityDurations.map((duration) => duration.duration)) +
		session.onboardingDuration +
		session.debriefingDuration
	);
}
export function getMaxCustomerDuration(session: Session): number {
	return (
		Math.max(...session.capacityDurations.map((duration) => duration.duration)) +
		session.onboardingDuration +
		session.debriefingDuration
	);
}

export function getSessionDurationForCapacity(session: Session, capacity: number): number {
	const duration = session.capacityDurations.find((duration) => {
		return capacity >= duration.min && capacity <= duration.max;
	});
	if (duration) {
		return duration.duration;
	} else {
		return 0;
	}
}

export function getMinCapacity(session: Session): number {
	// Min capacity of a session the smallest Areas capacity limited by the min capacity of the session

	// Limit defined on the session
	return Math.min(...session.capacityDurations.map((duration) => duration.min));

	// Areas min is 1
}
export const getSessionMaxCapacity = derived([areaStore], ([$areaStore]) => {
	return (session: Session): number => {
		// Max capacity of a session the biggest Areas capacity limited by the max capacity of the session

		// Limit defined on the session
		const maxCapacityDuration = Math.max(
			...session.capacityDurations.map((duration) => duration.max)
		);

		// Find sessions' areas that are not excluded
		const areas = session.followAllAreas
			? $areaStore.filter((area) => area.parentId === session.parentId)
			: (session.areas
					.map((area) => $areaStore.find((a) => a.id === area))
					.filter((a) => a !== undefined) as Area[]);

		const maxAreaCapacities = Math.max(...areas.map((area) => area.capacity ?? 0));

		// The max capacity allowed is the minimum of the max capacity of the session and the max capacity of the parent activity
		return Math.min(maxCapacityDuration, maxAreaCapacities);
	};
});

export function createNewSession(activityId: string): Session {
	const activity = get(activityStore).find((a) => a.id === activityId);
	const area = get(areaStore).find((a) => a.parentId === activityId);
	return {
		id: generateUUID(),
		parentId: activityId,
		name: '',
		color: getRandomPartialColor(),
		capacityDurations: [
			{
				min: 1,
				max: area?.capacity ?? 1, // Default to 1 if no area is found
				duration: 30 // Default duration in minutes
			}
		],
		preparationDuration: 0,
		onboardingDuration: 0,
		debriefingDuration: 0,
		cleaningDuration: 0,
		bookingTimeBefore: 0,
		followLogo: true,
		logo: null,
		followAllAreas: true,
		areas: [],
		archivedAt: null,
		interval: 15,
		maxBooking: 1
	};
}

export function getAreasForSession(session: Session): Area[] {
	// If session.followAllAreas is true, return the areas of the parent activity, otherwise return the areas of the session
	if (session.followAllAreas) {
		return get(areaStore).filter((area) => area.parentId === session.parentId);
	} else {
		return session.areas
			.map((aId) => get(areaStore).find((area) => area.id === aId))
			.filter((a) => a !== undefined) as Area[];
	}
}
